import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { useLang } from '../context/LangContext';
import { exportDb, importDb, getDbStats, DbStats, DbImportReport, exportScenarioBundle, importScenarioBundle, ScenarioBundleImportResponse } from '../api/db';
import { getTests } from '../api/tests';
import { Test } from '../types/testData';
import { MAX_IMPORT_BYTES } from '../constants';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.xl};
  display: flex;
  justify-content: center;
`;

const Box = styled.div`
  width: 100%;
  max-width: 860px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  flex: 1;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
  margin: 0 0 ${({ theme }) => theme.spacing.lg};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.lg};
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`;

const Stat = styled.div`
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textLight};
  margin-top: 4px;
`;

const CardBody = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const CardTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const CardDesc = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 0.9rem;
  margin: 0;
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.error};
  margin: 8px 0 0;
`;

const SuccessText = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
  margin: 8px 0 0;
`;

const ProgressWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textLight};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 10px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.border};
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.primary};
  transition: width 0.15s ease;
`;

const indeterminateKeyframes = keyframes`
  0% { margin-left: -30%; }
  100% { margin-left: 100%; }
`;

const ProgressFillIndeterminate = styled.div`
  height: 100%;
  width: 30%;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.primary};
  animation: ${indeterminateKeyframes} 1.2s ease-in-out infinite;
`;

const FileLabel = styled.label`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.textLight};
  transition: all 0.2s;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  user-select: none;
`;

const WarningText = styled.p`
  color: ${({ theme }) => theme.colors.warning};
  margin: 8px 0 0;
  font-size: 0.9rem;
`;

const HintText = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
  margin: 8px 0 0;
  font-size: 0.85rem;
`;

const PdfInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text};
`;

const FileInput = styled.input`
  display: none;
`;

const Select = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.95rem;
`;

const ReportTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: ${({ theme }) => theme.spacing.md};
  font-size: 0.9rem;
`;

const ReportTh = styled.th`
  text-align: left;
  padding: 6px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textLight};
`;

const ReportTd = styled.td`
  padding: 6px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const STAT_TABLES: { key: keyof DbStats }[] = [
  { key: 'users' },
  { key: 'tests' },
  { key: 'questions' },
  { key: 'answer_options' },
  { key: 'results' },
  { key: 'conclusions' },
  { key: 'payments' },
  { key: 'custom_answer_reviews' },
  { key: 'pdfs' },
];

const DbTransferPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const [stats, setStats] = useState<DbStats | null>(null);
  const [statsError, setStatsError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [importError, setImportError] = useState('');
  const [report, setReport] = useState<DbImportReport | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [includePdfs, setIncludePdfs] = useState(false);
  const [exportedSize, setExportedSize] = useState<number | null>(null);

  // Перенос сценария с графикой
  const [tests, setTests] = useState<Test[]>([]);
  const [selectedTestId, setSelectedTestId] = useState<number | null>(null);
  const [scenarioExporting, setScenarioExporting] = useState(false);
  const [scenarioImporting, setScenarioImporting] = useState(false);
  const [scenarioFile, setScenarioFile] = useState<File | null>(null);
  const [scenarioExportProgress, setScenarioExportProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [scenarioImportProgress, setScenarioImportProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [scenarioError, setScenarioError] = useState('');
  const [scenarioBundleResult, setScenarioBundleResult] = useState<ScenarioBundleImportResponse | null>(null);
  const [scenarioExportedSize, setScenarioExportedSize] = useState<number | null>(null);

  React.useEffect(() => {
    if (!loaded) {
      setLoaded(true);
      void (async () => {
        try {
          const s = await getDbStats();
          setStats(s);
          setStatsError('');
        } catch {
          setStatsError(t('dbImportError'));
        }
        try {
          const ts = await getTests();
          setTests(ts);
          setSelectedTestId(ts[0]?.id ?? null);
        } catch {
          setTests([]);
        }
      })();
    }
  }, [t, loaded]);

  const handleExport = async () => {
    setExporting(true);
    setImportError('');
    setExportProgress({ loaded: 0, total: 0 });
    try {
      const res = await exportDb(includePdfs, (loaded, total) => setExportProgress({ loaded, total }));
      setExportedSize(res.size);
    } catch {
      setImportError(t('dbImportError'));
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setImportError('');
    setReport(null);
    setImportProgress({ loaded: 0, total: file.size });
    try {
      const res = await importDb(file, (loaded, total) => setImportProgress({ loaded, total }));
      setReport(res.report);
      const s = await getDbStats();
      setStats(s);
    } catch {
      setImportError(t('dbImportError'));
    } finally {
      setImporting(false);
    }
  };

  const acceptFile = (f: File | null) => {
    if (!f) return;
    if (!/\.json$/i.test(f.name)) {
      setImportError(t('dbImportError'));
      return;
    }
    if (f.size > MAX_IMPORT_BYTES) {
      setFile(null);
      setImportError(t('dbFileTooLarge').replace('{size}', fmtBytes(f.size)));
      return;
    }
    setFile(f);
    setImportError('');
    setReport(null);
  };

  const handleScenarioExport = async () => {
    if (!selectedTestId) return;
    const test = tests.find((x) => x.id === selectedTestId);
    setScenarioExporting(true);
    setScenarioError('');
    setScenarioExportProgress({ loaded: 0, total: 0 });
    setScenarioExportedSize(null);
    try {
      const size = await exportScenarioBundle(selectedTestId, test?.title ?? '', (loaded, total) => setScenarioExportProgress({ loaded, total }));
      setScenarioExportedSize(size);
    } catch {
      setScenarioError(t('dbScenarioExportError'));
    } finally {
      setScenarioExporting(false);
    }
  };

  const handleScenarioImport = async () => {
    if (!scenarioFile) return;
    setScenarioImporting(true);
    setScenarioError('');
    setScenarioBundleResult(null);
    setScenarioImportProgress({ loaded: 0, total: scenarioFile.size });
    try {
      const res = await importScenarioBundle(scenarioFile, (loaded, total) => setScenarioImportProgress({ loaded, total }));
      setScenarioBundleResult(res);
      const s = await getDbStats();
      setStats(s);
    } catch {
      setScenarioError(t('dbScenarioImportError'));
    } finally {
      setScenarioImporting(false);
    }
  };

  const acceptScenario = (f: File | null) => {
    if (!f) return;
    if (!/\.zip$/i.test(f.name)) {
      setScenarioError(t('dbScenarioBadFile'));
      return;
    }
    if (f.size > MAX_IMPORT_BYTES) {
      setScenarioFile(null);
      setScenarioError(t('dbFileTooLarge').replace('{size}', fmtBytes(f.size)));
      return;
    }
    setScenarioFile(f);
    setScenarioError('');
    setScenarioBundleResult(null);
  };

  const statLabels: Record<keyof DbStats, string> = {
    users: 'Пользователи',
    tests: 'Тесты',
    questions: 'Вопросы',
    answer_options: 'Варианты',
    results: 'Результаты',
    conclusions: 'Заключения',
    payments: 'Оплаты',
    custom_answer_reviews: 'Проверки',
    pdfs: 'PDF',
  };

  const fmtBytes = (b: number): string => {
    if (b >= 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} МБ`;
    if (b >= 1024) return `${Math.round(b / 1024)} КБ`;
    return `${Math.round(b)} Б`;
  };

  const fmtPct = (p: { loaded: number; total: number } | null): number => {
    if (!p || !p.total) return 0;
    return Math.min(100, Math.max(0, Math.round((p.loaded / p.total) * 100)));
  };

  return (
    <PageContainer>
      <Box>
        <Header>
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>{t('backBtn')}</Button>
          <Title>{t('dbTransferTitle')}</Title>
        </Header>
        <Subtitle>{t('dbTransferSubtitle')}</Subtitle>

        <StatCard>
          <CardTitle>{t('dbStatsLabel')}</CardTitle>
          <StatGrid>
            {STAT_TABLES.map(({ key }) => (
              <Stat key={key}>
                <StatValue>{stats ? stats[key] : '—'}</StatValue>
                <StatLabel>{statLabels[key]}</StatLabel>
              </Stat>
            ))}
          </StatGrid>
          {statsError && <ErrorText>{statsError}</ErrorText>}
        </StatCard>

        <Grid>
          <CardBody>
            <CardTitle>{t('dbExportLabel')}</CardTitle>
            <CardDesc>{t('dbExportDesc')}</CardDesc>
            <CheckboxRow>
              <input
                type="checkbox"
                checked={includePdfs}
                onChange={(e) => setIncludePdfs(e.target.checked)}
                disabled={exporting}
              />
              {t('dbIncludePdfs')}
            </CheckboxRow>
            <HintText>
              {t('dbMaxArchiveSize').replace('{size}', fmtBytes(MAX_IMPORT_BYTES))}
            </HintText>
            <Button variant="primary" size="lg" onClick={handleExport} disabled={exporting}>
              {exporting ? t('dbExportLoading') : t('dbExportBtn')}
            </Button>
            {exporting && (
              <ProgressWrap>
                <ProgressLabel>
                  <span>{t('dbProgressDownload')}</span>
                  {exportProgress && exportProgress.total ? (
                    <span>
                      {fmtBytes(exportProgress.loaded)}
                      {' / '}{fmtBytes(exportProgress.total)}
                      {' · '}{fmtPct(exportProgress)}%
                    </span>
                  ) : (
                    <span>{t('dbProgressWait')}</span>
                  )}
                </ProgressLabel>
                <ProgressBar>
                  {exportProgress && exportProgress.total ? (
                    <ProgressFill style={{ width: `${fmtPct(exportProgress)}%` }} />
                  ) : (
                    <ProgressFillIndeterminate />
                  )}
                </ProgressBar>
              </ProgressWrap>
            )}
            {exportedSize !== null && (
              <HintText>
                {t('dbExportedSize').replace('{size}', fmtBytes(exportedSize))}
              </HintText>
            )}
            {exportedSize !== null && exportedSize > MAX_IMPORT_BYTES * 0.8 && (
              <WarningText>
                {t('dbNearLimit').replace('{size}', fmtBytes(exportedSize)).replace('{limit}', fmtBytes(MAX_IMPORT_BYTES))}
              </WarningText>
            )}
          </CardBody>
          <CardBody>
            <CardTitle>{t('dbImportLabel')}</CardTitle>
            <CardDesc>{t('dbImportDesc')}</CardDesc>
            <FileLabel
              onDrop={(e) => { e.preventDefault(); acceptFile(e.dataTransfer.files?.[0] || null); }}
            >
              <FileInput
                type="file"
                accept=".json"
                onChange={(e) => acceptFile(e.target.files?.[0] || null)}
              />
              {file ? t('dbImportFileSelected').replace('{name}', file.name) : t('dbImportDrop')}
            </FileLabel>
            <HintText>
              {t('dbMaxArchiveSize').replace('{size}', fmtBytes(MAX_IMPORT_BYTES))}
            </HintText>
            <Button variant="secondary" size="lg" onClick={handleImport} disabled={!file || importing}>
              {importing ? t('dbImportLoading') : t('dbImportBtn')}
            </Button>
            {importing && (
              <ProgressWrap>
                <ProgressLabel>
                  <span>{t('dbProgressUpload')}</span>
                  {importProgress && importProgress.total ? (
                    <span>
                      {fmtBytes(importProgress.loaded)}
                      {' / '}{fmtBytes(importProgress.total)}
                      {' · '}{fmtPct(importProgress)}%
                    </span>
                  ) : (
                    <span>{t('dbProgressWait')}</span>
                  )}
                </ProgressLabel>
                <ProgressBar>
                  {importProgress && importProgress.total ? (
                    <ProgressFill style={{ width: `${fmtPct(importProgress)}%` }} />
                  ) : (
                    <ProgressFillIndeterminate />
                  )}
                </ProgressBar>
              </ProgressWrap>
            )}
            {importError && <ErrorText>{importError}</ErrorText>}
          </CardBody>
        </Grid>

        <Card style={{ marginTop: 20 }}>
          <CardTitle>{t('dbScenarioTitle')}</CardTitle>
          <CardDesc>{t('dbScenarioDesc')}</CardDesc>
          <Grid>
            <CardBody>
              <CardTitle>{t('dbScenarioExportLabel')}</CardTitle>
              <CardDesc>{t('dbScenarioExportDesc')}</CardDesc>
              <Select value={selectedTestId ?? ''} onChange={(e) => setSelectedTestId(Number(e.target.value))} disabled={scenarioExporting}>
                {tests.length === 0 ? (
                  <option value="">
                    {t('dbScenarioNoTests')}
                  </option>
                ) : (
                  tests.map((x) => (
                    <option key={x.id} value={x.id}>{x.title}</option>
                  ))
                )}
              </Select>
              <HintText>{t('dbScenarioExportHint')}</HintText>
              <Button variant="primary" size="lg" onClick={handleScenarioExport} disabled={!selectedTestId || scenarioExporting}>
                {scenarioExporting ? t('dbScenarioExporting') : t('dbScenarioExportBtn')}
              </Button>
              {scenarioExporting && (
                <ProgressWrap>
                  <ProgressLabel>
                    <span>{t('dbProgressDownload')}</span>
                    {scenarioExportProgress && scenarioExportProgress.total ? (
                      <span>
                        {fmtBytes(scenarioExportProgress.loaded)}
                        {' / '}{fmtBytes(scenarioExportProgress.total)}
                        {' · '}{fmtPct(scenarioExportProgress)}%
                      </span>
                    ) : (
                      <span>{t('dbProgressWait')}</span>
                    )}
                  </ProgressLabel>
                  <ProgressBar>
                    {scenarioExportProgress && scenarioExportProgress.total ? (
                      <ProgressFill style={{ width: `${fmtPct(scenarioExportProgress)}%` }} />
                    ) : (
                      <ProgressFillIndeterminate />
                    )}
                  </ProgressBar>
                </ProgressWrap>
              )}
              {scenarioExportedSize !== null && (
                <HintText>{t('dbExportedSize').replace('{size}', fmtBytes(scenarioExportedSize))}</HintText>
              )}
            </CardBody>
            <CardBody>
              <CardTitle>{t('dbScenarioImportLabel')}</CardTitle>
              <CardDesc>{t('dbScenarioImportDesc')}</CardDesc>
              <FileLabel
                onDrop={(e) => { e.preventDefault(); acceptScenario(e.dataTransfer.files?.[0] || null); }}
              >
                <FileInput
                  type="file"
                  accept=".zip"
                  onChange={(e) => acceptScenario(e.target.files?.[0] || null)}
                />
                {scenarioFile ? t('dbImportFileSelected').replace('{name}', scenarioFile.name) : t('dbScenarioDrop')}
              </FileLabel>
              <HintText>
                {t('dbMaxArchiveSize').replace('{size}', fmtBytes(MAX_IMPORT_BYTES))}
              </HintText>
              <Button variant="secondary" size="lg" onClick={handleScenarioImport} disabled={!scenarioFile || scenarioImporting}>
                {scenarioImporting ? t('dbScenarioImporting') : t('dbScenarioImportBtn')}
              </Button>
              {scenarioImporting && (
                <ProgressWrap>
                  <ProgressLabel>
                    <span>{t('dbProgressUpload')}</span>
                    {scenarioImportProgress && scenarioImportProgress.total ? (
                      <span>
                        {fmtBytes(scenarioImportProgress.loaded)}
                        {' / '}{fmtBytes(scenarioImportProgress.total)}
                        {' · '}{fmtPct(scenarioImportProgress)}%
                      </span>
                    ) : (
                      <span>{t('dbProgressWait')}</span>
                    )}
                  </ProgressLabel>
                  <ProgressBar>
                    {scenarioImportProgress && scenarioImportProgress.total ? (
                      <ProgressFill style={{ width: `${fmtPct(scenarioImportProgress)}%` }} />
                    ) : (
                      <ProgressFillIndeterminate />
                    )}
                  </ProgressBar>
                </ProgressWrap>
              )}
              {scenarioError && <ErrorText>{scenarioError}</ErrorText>}
              {scenarioBundleResult && (
                <HintText>
                  {scenarioBundleResult.skipped
                    ? t('dbScenarioResultDuplicate').replace('{title}', tests.find((x) => x.id === scenarioBundleResult.test_id)?.title ?? String(scenarioBundleResult.test_id))
                    : t('dbScenarioResultOk')
                      .replace('{questions}', String(scenarioBundleResult.questions_added))
                      .replace('{options}', String(scenarioBundleResult.options_added))
                      .replace('{media_added}', String(scenarioBundleResult.media_added))
                      .replace('{media_skipped}', String(scenarioBundleResult.media_skipped))}
                  {scenarioBundleResult.media_missing.length > 0 && (
                    <WarningText style={{ marginTop: 6 }}>
                      {t('dbScenarioMediaMissing').replace('{n}', String(scenarioBundleResult.media_missing.length))}
                    </WarningText>
                  )}
                </HintText>
              )}
            </CardBody>
          </Grid>
        </Card>

        {report && (
          <Card style={{ marginTop: 20 }}>
            <CardTitle>{t('dbImportSuccess')}</CardTitle>
            <ReportTable>
              <thead>
                <tr>
                  <ReportTh>Раздел</ReportTh>
                  <ReportTh>{t('dbImportReportAdded')}</ReportTh>
                  <ReportTh>{t('dbImportReportSkipped')}</ReportTh>
                  <ReportTh>{t('dbImportReportErrors')}</ReportTh>
                </tr>
              </thead>
              <tbody>
                {Object.entries(report).map(([table, info]) => (
                  <tr key={table}>
                    <ReportTd>{table === '_pdfs' ? t('dbReportPdf') : table}</ReportTd>
                    <ReportTd>{info.added}</ReportTd>
                    <ReportTd>{info.skipped ?? 0}</ReportTd>
                    <ReportTd>{info.errors ?? 0}</ReportTd>
                  </tr>
                ))}
              </tbody>
            </ReportTable>
            {report._pdfs && report._pdfs.added === 0 && (
              <PdfInfo style={{ marginTop: 10 }}>
                <span>{t('dbPdfNotFound')}</span>
              </PdfInfo>
            )}
          </Card>
        )}
      </Box>
    </PageContainer>
  );
};

export default DbTransferPage;